import dayjs from 'dayjs'

import TIMEZONE_NAMES from './Constants/TimezonesNames'

export const convertToUtc = (dateString) => {
    if (!dateString) {
        return ''
    }

    if (dateString.includes('T')) {
        return dateString
    }

    // else : old no timezone format (considered as Europe/Paris)

    const format = dateString.includes('/') ? 'DD/MM/YYYY H:mm' : 'YYYY-MM-DD H:mm'

    const result = dayjs.utc(dateString, format).tz('Europe/Paris')

    return result.toISOString()
}

export const dateObjectConvertToUtc = (dateObject) =>
    dateObject ? convertToUtc(`${dateObject.day} ${dateObject.hour}`) : ''

export const toDate = (dateString) => (dateString ? dayjs(convertToUtc(dateString)).tz() : null)

export const toDateFromTime = (timeString = '00:00:00', date) => {
    const tzFree = `${dayjs(date).format('YYYY-MM-DD')}T${timeString}.000Z`
    const res = dayjs(tzFree).tz()
    return res
}

export const toDateFromTimeWithDate = (date, timeString = '00:00:00') => {
    if (!date) {
        return toDateFromTime(timeString)
    }
    const tzFree = `${dayjs(date).format('YYYY-MM-DD')}T${timeString}.000Z`
    const res = dayjs(tzFree).tz()
    return res
}

export const toDateFromTimeWithTimzone = (tz, timeString = '00:00:00') => {
    const offset = dayjs().tz(tz).format('Z')
    const tzFree = `${dayjs().format('YYYY-MM-DD')}T${timeString}${offset}`
    const res = dayjs(tzFree).utc()
    return res
}

export const toDateFromTimeTzFree = (timeString = '00:00:00') => {
    const parts = timeString.split(':')
    const res = dayjs().set('hour', parts[0]).set('minute', parts[1]).set('second', parts[2])
    return res
}

export const toUtc = (dateObject) => dateObject?.utc()?.format() || null

export const dateNow = () => dayjs().tz()

export const GetEventDateFormated = (startISO, endISO) => {
    if (!startISO && !endISO) {
        return
    }

    const startDJS = dayjs(startISO).tz()
    const endDJS = dayjs(endISO).tz()

    if (!startDJS.isValid() && !endDJS.isValid()) {
        return
    }

    if (startISO && !endISO) {
        return `${startDJS.format('MMMM DD, YYYY')}`
    }
    if (!startISO && endISO) {
        return `${endDJS.format('MMMM DD, YYYY')}`
    }

    if (startDJS.isSame(endISO)) {
        return `${startDJS.format('MMMM DD, YYYY')}`
    }

    if (startDJS.year() !== endDJS.year()) {
        return `${startDJS.format('MMMM DD, YYYY')} - ${endDJS.format('MMMM DD, YYYY')}`
    }
    if (startDJS.month() !== endDJS.month()) {
        return `${startDJS.format('MMMM DD')} - ${endDJS.format('MMMM DD, YYYY')}`
    }
    if (startDJS.date() === endDJS.date()) {
        return `${startDJS.format('MMMM DD, YYYY')}`
    }

    return `${startDJS.format('MMMM DD')}-${endDJS.format('DD, YYYY')}`
}

export const convertToFormat = (dateString, format) => dayjs(convertToUtc(dateString)).tz().format(format)

export const convertToSlashDateTimeFormat = (dateString, withSeconds = false) =>
    convertToFormat(dateString, 'DD/MM/YYYY HH:mm' + (withSeconds ? ':ss' : ''))

export const convertToSlashDateFormat = (dateString) => convertToFormat(dateString, 'DD/MM/YYYY')

export const tzOffsetGuess = (date) => {
    const tz = dayjs.tz.guess()
    const offset = dayjs(date).tz(tz).format('Z')

    return `${tz} (${offset})`
}

export const tzOffset = (tz, date) => {
    if (!tz) {
        return tzOffsetGuess(date)
    }
    const offset = dayjs(date).tz().format('Z')

    return `${tz} (${offset})`
}

export const isFutureDate = (dateString) => toDate(dateString)?.isAfter(dateNow())

/** `tz` when this browser's Intl knows it, else '' — profile zones are free text in the DB. */
export const validTimezone = (tz) => {
    try {
        return tz && new Intl.DateTimeFormat('en', { timeZone: tz }) ? tz : ''
    } catch {
        return ''
    }
}

export const getAllTzs = () =>
    TIMEZONE_NAMES.flatMap((name) => {
        try {
            return [{ name, offset: dayjs().tz(name).format('Z') }]
        } catch {
            return [] // a zone this browser's ICU lacks is left out instead of crashing the page
        }
    })

// Localized event dates: Intl.DateTimeFormat already knows month names and word order per
// language ("Jun 7, 2026" / "7 juin 2026" / "7 de jun. de 2026"), so no dayjs locale import
// is needed (those are disabled app-wide, see index.jsx). `tz` is the event timezone; without
// one the browser zone is used. An unknown timezone falls back to the browser zone.
const DATE_PARTS = { day: 'numeric', month: 'short', year: 'numeric' }

const localDateFormatter = (language, tz) => {
    const locale = language || 'en'
    try {
        return new Intl.DateTimeFormat(locale, tz ? { ...DATE_PARTS, timeZone: tz } : DATE_PARTS)
    } catch {
        return new Intl.DateTimeFormat(locale, DATE_PARTS)
    }
}

const toValidDate = (iso) => {
    if (!iso) return null
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? null : date
}

/** "Jun 7, 2026" in the user's language, in `tz`; '' when the date is missing or invalid. */
export const formatLocalDate = (iso, { language, tz } = {}) => {
    const date = toValidDate(iso)
    return date ? localDateFormatter(language, tz).format(date) : ''
}

/**
 * "Jun 7 – 8, 2026" / "7–8 juin 2026" / "Jun 7 – Jul 2, 2026" / "Jun 7, 2026 – Jan 2, 2027".
 * A missing or invalid end (or an end on the same day) gives the single start date.
 */
export const formatLocalDateRange = (startIso, endIso, { language, tz } = {}) => {
    const start = toValidDate(startIso)
    if (!start) return ''
    const end = toValidDate(endIso)
    const formatter = localDateFormatter(language, tz)
    if (!end || end < start || formatter.format(start) === formatter.format(end)) return formatter.format(start)
    if (typeof formatter.formatRange !== 'function') return `${formatter.format(start)} - ${formatter.format(end)}`
    // Intl pads the dash with thin / narrow no-break spaces; plain spaces render the same everywhere.
    return formatter.formatRange(start, end).replace(/[\u2009\u202f]/g, ' ')
}

/**
 * Organizer invoice issue date (DD/MM/YYYY) from the invoiced period's end date. Periods from July 2026 are
 * dated the last day of the month (accountant request, Oct 2026); earlier ones keep the 1st of the next month
 * they were issued with, so re-downloading an old invoice never changes its date.
 */
export const invoiceIssueDate = (periodEnd) => {
    const parisDay = new Date(periodEnd).toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' }) // YYYY-MM-DD
    if (parisDay < '2026-07-01') return dayjs(periodEnd).add(1, 'month').startOf('month').format('DD/MM/YYYY')
    return parisDay.split('-').reverse().join('/')
}
